import type { Fetcher } from '@/lib/api/fetcher';

import type {
  Deposit,
  FeedRun,
  Policy,
  PolicyOut,
  Recurrence,
  WaitlistDraft,
  WaitlistEntry,
  WaitlistPage,
} from './schemas';
import {
  depositOrNullSchema,
  depositSchema,
  feedRunListSchema,
  policySchema,
  recurrenceSchema,
  waitlistEntrySchema,
  waitlistPageSchema,
} from './schemas';

/**
 * One function per BP-25 §5 route the §6 screens use.
 *
 * **Every write that creates money takes an `Idempotency-Key` from the
 * caller** rather than minting one here, the rule `features/billing`
 * set: a retry has to send the *same* key, and only the screen that
 * owns the button knows whether this click is the first or the retry.
 * A double-tapped "Ask for a deposit" is two payment requests to one
 * client for one appointment.
 *
 * **Joining a waitlist does not take one.** It creates no money and no
 * message; a duplicate entry is a row the sweep passes over, and §5
 * asks for no key on it.
 */
export interface BookingExtrasApi {
  readDeposit: (bookingId: string) => Promise<Deposit | null>;
  requestDeposit: (bookingId: string, amountPaise: number | null, key: string) => Promise<Deposit>;
  applyDeposit: (depositId: string, invoiceId: string, key: string) => Promise<Deposit>;
  forfeitDeposit: (depositId: string, reason: string, key: string) => Promise<Deposit>;
  listWaitlist: (status?: string) => Promise<WaitlistPage>;
  joinWaitlist: (draft: WaitlistDraft) => Promise<WaitlistEntry>;
  cancelWaitlist: (entryId: string) => Promise<void>;
  startRecurrence: (bookingId: string, rrule: string, key: string) => Promise<Recurrence>;
  readRecurrence: (recurrenceId: string) => Promise<Recurrence>;
  stopRecurrence: (recurrenceId: string) => Promise<Recurrence>;
  splitRecurrence: (recurrenceId: string, bookingId: string, rrule: string) => Promise<Recurrence>;
  listFeeds: () => Promise<FeedRun[]>;
  regenerateFeeds: () => Promise<FeedRun[]>;
  readPolicy: () => Promise<PolicyOut>;
  savePolicy: (policy: Policy) => Promise<PolicyOut>;
  /** The client's own, on BP-11's `/me` router. */
  listMine: () => Promise<WaitlistPage>;
  joinMine: (draft: WaitlistDraft) => Promise<WaitlistEntry>;
  leaveMine: (entryId: string) => Promise<void>;
}

export const PREFIX = '/v1';

/** The §5 routes over the shared transport. */
export function createBookingExtrasApi(fetcher: Fetcher): BookingExtrasApi {
  const idem = (key: string): Record<string, string> => ({ 'Idempotency-Key': key });
  return {
    readDeposit: (bookingId) =>
      fetcher.json(
        `${PREFIX}/bookings/${bookingId}/deposit`,
        { method: 'GET' },
        depositOrNullSchema,
      ),
    requestDeposit: (bookingId, amountPaise, key) =>
      fetcher.json(
        `${PREFIX}/bookings/${bookingId}/deposit`,
        {
          method: 'POST',
          body: amountPaise === null ? {} : { amount_paise: amountPaise },
          headers: idem(key),
        },
        depositSchema,
      ),
    applyDeposit: (depositId, invoiceId, key) =>
      fetcher.json(
        `${PREFIX}/deposits/${depositId}/apply?invoice_id=${invoiceId}`,
        { method: 'POST', headers: idem(key) },
        depositSchema,
      ),
    forfeitDeposit: (depositId, reason, key) =>
      fetcher.json(
        `${PREFIX}/deposits/${depositId}/forfeit`,
        { method: 'POST', body: { reason }, headers: idem(key) },
        depositSchema,
      ),
    listWaitlist: (status) =>
      fetcher.json(
        `${PREFIX}/waitlist${status === undefined ? '' : `?status=${status}`}`,
        { method: 'GET' },
        waitlistPageSchema,
      ),
    joinWaitlist: (draft) =>
      fetcher.json(`${PREFIX}/waitlist`, { method: 'POST', body: draft }, waitlistEntrySchema),
    cancelWaitlist: (entryId) =>
      fetcher.empty(`${PREFIX}/waitlist/${entryId}`, { method: 'DELETE' }),
    startRecurrence: (bookingId, rrule, key) =>
      fetcher.json(
        `${PREFIX}/bookings/${bookingId}/recurrence`,
        { method: 'POST', body: { rrule }, headers: idem(key) },
        recurrenceSchema,
      ),
    readRecurrence: (recurrenceId) =>
      fetcher.json(`${PREFIX}/recurrences/${recurrenceId}`, { method: 'GET' }, recurrenceSchema),
    stopRecurrence: (recurrenceId) =>
      fetcher.json(
        `${PREFIX}/recurrences/${recurrenceId}`,
        { method: 'PATCH', body: { stop: true } },
        recurrenceSchema,
      ),
    splitRecurrence: (recurrenceId, bookingId, rrule) =>
      fetcher.json(
        `${PREFIX}/recurrences/${recurrenceId}`,
        { method: 'PATCH', body: { rrule, booking_id: bookingId } },
        recurrenceSchema,
      ),
    listFeeds: () => fetcher.json(`${PREFIX}/rwg/feeds`, { method: 'GET' }, feedRunListSchema),
    regenerateFeeds: () =>
      fetcher.json(`${PREFIX}/rwg/feeds`, { method: 'POST' }, feedRunListSchema),
    readPolicy: () =>
      fetcher.json(`${PREFIX}/settings/booking-extras`, { method: 'GET' }, policySchema),
    savePolicy: (policy) =>
      fetcher.json(
        `${PREFIX}/settings/booking-extras`,
        { method: 'PUT', body: policy },
        policySchema,
      ),
    listMine: () => fetcher.json(`${PREFIX}/me/waitlist`, { method: 'GET' }, waitlistPageSchema),
    joinMine: (draft) =>
      fetcher.json(`${PREFIX}/me/waitlist`, { method: 'POST', body: draft }, waitlistEntrySchema),
    leaveMine: (entryId) => fetcher.empty(`${PREFIX}/me/waitlist/${entryId}`, { method: 'DELETE' }),
  };
}
